<template>
  <div class="gid-appointment position-relative">
    <p v-if="disclaimer" class="w-75">{{ disclaimer }}</p>
    <div class="d-flex flex-wrap flex-sm-nowrap align-items-center">
      <template v-if="!multiday">
        <span class="mr-2">{{ $t('inputs.appointment.date') }}</span>
        <date-picker
          :class="startClasses"
          :readonly="readonly"
          v-model="appointment.date"
          :config="date_options"
          @dp-change="updateDate"
        />
      </template>
      <span :class="['mr-2', { 'text-capitalize': multiday }]">
        {{ $t('inputs.appointment.between') }}
      </span>
      <date-picker
        :class="startClasses"
        :readonly="readonly"
        v-model="appointment.start_time"
        :config="multiday ? datetime_start_options : time_options"
        @dp-change="updateStartTime"
      />
      <span class="mr-2">{{ $t('inputs.appointment.and') }}</span>
      <date-picker
        :class="endClasses"
        :readonly="readonly"
        v-model="appointment.end_time"
        :config="multiday ? datetime_end_options : time_end_options"
        @dp-change="updateEndTime"
      />
    </div>
    <b-form-invalid-feedback :state="defaultValidationState">
      <ul
        v-if="
          Array.isArray(defaultValidationErrorMessages) &&
          defaultValidationErrorMessages.length > 1
        "
      >
        <li
          v-for="(error, index) in defaultValidationErrorMessages"
          :key="index"
        >
          {{ error }}
        </li>
      </ul>
      <template v-else>{{ defaultValidationErrorMessages }}</template>
    </b-form-invalid-feedback>
    <div
      v-if="readiness_date"
      class="d-flex flex-wrap flex-sm-nowrap align-items-center"
    >
      <span>
        {{ $t('inputs.appointment.readiness_date') }}:
        {{ readiness_date.toLocaleDateString() }}
      </span>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import DatePicker from 'vue-bootstrap-datetimepicker';
import BaseInput from './mixins/BaseInput';
import ValidatingInput from '../mixins/ValidatingInput';

export default {
  mixins: [BaseInput, ValidatingInput],
  components: {
    DatePicker,
  },
  props: {
    disableMinDateConstrains: Boolean,
    multiday: Boolean,
    readiness_date: Date,
    disclaimer: String,
  },
  data() {
    const dateTimePickerIcons = {
      time: 'material-icons access_time',
      date: 'material-icons date_range',
      up: 'material-icons keyboard_arrow_up',
      down: 'material-icons keyboard_arrow_down',
      previous: 'material-icons keyboard_arrow_left',
      next: 'material-icons keyboard_arrow_right',
      today: 'material-icons today',
      clear: 'material-icons clear',
      close: 'material-icons close',
    };
    return {
      appointment: {
        date: null,
        start_time: null,
        end_time: null,
      },
      date_options: {
        format: 'L',
        locale: moment.locale(),
        icons: dateTimePickerIcons,
        minDate: false,
      },
      time_options: {
        format: 'HH:mm',
        locale: moment.locale(),
        stepping: 15,
        icons: dateTimePickerIcons,
      },
      time_end_options: {
        format: 'HH:mm',
        locale: moment.locale(),
        stepping: 15,
        icons: dateTimePickerIcons,
        minDate: false,
      },
      datetime_start_options: {
        format: 'L HH:mm',
        locale: moment.locale(),
        stepping: 15,
        icons: dateTimePickerIcons,
        minDate: false,
      },
      datetime_end_options: {
        format: 'L HH:mm',
        stepping: 15,
        locale: moment.locale(),
        icons: dateTimePickerIcons,
        minDate: false,
      },
    };
  },
  computed: {
    startClasses() {
      return [
        'mr-0',
        'mr-sm-2',
        'my-2',
        'gid-appointment__date-picker',
        { 'is-invalid': this.defaultValidationState === false },
      ];
    },
    endClasses() {
      return [
        'my-2',
        'gid-appointment__date-picker',
        { 'is-invalid': this.defaultValidationState === false },
      ];
    },
    minDate() {
      return !this.disableMinDateConstrains
        ? this.$store.getters.jobInputsConstrainMinDate(this.jobId)
        : false;
    },
  },
  watch: {
    minDate: function (value) {
      this.date_options.minDate = value;
      this.datetime_start_options.minDate = value;
    },
  },
  created() {
    let startDate;
    const val = this.passedValue;
    if (this.minDate) {
      this.date_options.minDate = this.minDate;
      this.datetime_start_options.minDate = this.minDate;
      startDate = this.minDate;
    } else {
      startDate = moment().startOf('day');
    }
    if (
      val &&
      val.start &&
      (moment(val.start).isSameOrAfter(startDate) ||
        this.disableMinDateConstrains)
    ) {
      if (this.multiday) {
        this.appointment.start_time = moment(val.start).format(
          this.datetime_start_options.format,
        );
        this.appointment.end_time = moment(val.end).format(
          this.datetime_end_options.format,
        );
      } else {
        this.appointment.date = moment(val.start).format(
          this.date_options.format,
        );
        this.appointment.start_time = moment(val.start).format(
          this.time_options.format,
        );
        this.appointment.end_time = moment(val.end).format(
          this.time_options.format,
        );
      }
    }
  },
  methods: {
    updateValue() {
      let start_datetime, end_datetime;
      if (this.multiday) {
        start_datetime = moment(
          this.appointment.start_time,
          this.datetime_start_options.format,
        );
        end_datetime = moment(
          this.appointment.end_time,
          this.datetime_end_options.format,
        );
      } else {
        const mixFormat =
          this.date_options.format + ' ' + this.time_options.format;
        start_datetime = moment(
          this.appointment.date + ' ' + this.appointment.start_time,
          mixFormat,
        );
        end_datetime = moment(
          this.appointment.date + ' ' + this.appointment.end_time,
          mixFormat,
        );
      }
      this.commitOrEmit(
        {
          start: start_datetime.toISOString(true),
          end: end_datetime.toISOString(true),
        },
        true,
      );
      this.defaultClearValidation();
    },
    updateDate() {
      this.updateValue();
    },
    updateStartTime(event) {
      if (event.date) {
        const newMinDate = event.date.add(30, 'minutes');
        this.time_end_options.minDate = newMinDate;
        this.datetime_end_options.minDate = newMinDate;
      } else {
        this.time_end_options.minDate = false;
        this.datetime_end_options.minDate = false;
      }
      this.updateValue();
    },
    updateEndTime() {
      this.updateValue();
    },
  },
};
</script>

<style lang="scss">
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins';
@import '../../scss/_datetimepicker.scss';

.gid-appointment {
  // Firefox refuses to shrink the input bellow certain size. Fix with setting min-width to 0
  &__date-picker {
    min-width: 0;
  }

  @include media-breakpoint-down(xs) {
    &:not(:last-child) {
      border-bottom: $border-width solid $border-color;
      padding-bottom: 1rem;
      margin-bottom: 1rem;
    }
  }
}
</style>
